import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  ShoppingCart,
  MapPin,
  Package,
  CheckCircle,
  Share2,
  Flag,
  X,
} from "lucide-react";
import "./ProductDetail.css";

const CATEGORY_LABELS = {
  notes: "Notes",
  gaming: "Gaming",
  clothing: "Clothing",
  electronics: "Electronics",
  books: "Books",
  sports: "Sports",
  food: "Food",
  other: "Other",
};

const REPORT_REASONS = [
  { value: "fake_product", label: "Fake or misleading product" },
  { value: "wrong_price", label: "Wrong / unrealistic price" },
  { value: "spam", label: "Spam listing" },
  { value: "inappropriate_content", label: "Inappropriate content" },
  { value: "scam", label: "Possible scam" },
  { value: "prohibited_item", label: "Prohibited item" },
  { value: "other", label: "Other" },
];

export default function ProductDetail() {
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [contacting, setContacting] = useState(false);
  const [sellerContact, setSellerContact] = useState(null);
  const [inCart, setInCart] = useState(false);
  const [addingCart, setAddingCart] = useState(false);

  // Report state
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);

  useEffect(() => {
    api
      .get("/products/" + id)
      .then(function (res) {
        setProduct(res.data.product);
      })
      .catch(function () {
        toast.error("Product not found.");
      })
      .finally(function () {
        setLoading(false);
      });
  }, [id]);

  const handleContact = async () => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    setContacting(true);
    try {
      const res = await api.post("/contact", { productId: id });
      setSellerContact(res.data);
      toast.success("Seller contact revealed!");
    } catch (err) {
      if (err.response?.data?.chatOnly) {
        toast("This seller is available via chat only.", { icon: "💬" });
      } else {
        toast.error(err.response?.data?.message || "Could not get contact.");
      }
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    setAddingCart(true);
    try {
      await api.post("/users/cart", { productId: id });
      setInCart(true);
      toast.success("Added to cart!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not add to cart.");
    } finally {
      setAddingCart(false);
    }
  };

  // ── Share product link ──────────────────────────────────
  const handleShare = async () => {
    const url = window.location.href;
    const text =
      "Check out this listing on GLA Exchange: " +
      (product?.title || "") +
      " — ₹" +
      (product?.price || "");

    // Native share API (mobile)
    if (navigator.share) {
      try {
        await navigator.share({ title: product?.title, text, url });
        return;
      } catch {}
    }

    // WhatsApp fallback
    const waUrl =
      "https://wa.me/?text=" + encodeURIComponent(text + "\n" + url);
    window.open(waUrl, "_blank");
    toast.success("Opening WhatsApp to share!");
  };

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => toast.success("Link copied to clipboard!"))
      .catch(() => toast.error("Could not copy link."));
  };

  const handleChat = () => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    const preText = `Hi! I'm interested in your listing: "${product.title}" priced at ₹${product.price.toLocaleString()}. Is it still available?`;
    navigate("/app/chat", { state: { sellerId: seller._id, preText } });
  };

  // ── Report product ──────────────────────────────────────
  const handleReport = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
      return;
    }
    if (!reportReason) {
      toast.error("Please select a reason.");
      return;
    }

    setReporting(true);
    try {
      await api.post("/reports", {
        targetType: "product",
        productId: id,
        reason: reportReason,
        details: reportDetails,
      });
      setReported(true);
      setShowReport(false);
      toast.success("Report submitted. Admin will review it.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit report.");
    } finally {
      setReporting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">😕</div>
        <h4>Product not found</h4>
        <Link
          to="/app/marketplace"
          className="btn btn-primary"
          style={{ marginTop: 8 }}
        >
          Back to Marketplace
        </Link>
      </div>
    );
  }

  const seller = product.seller;
  const isOwn = user && seller && seller._id === user._id;
  const isRemoved = product.status !== "active";

  const waLink =
    "https://wa.me/91" + (sellerContact ? sellerContact.whatsappNumber : "");
  const callLink = "tel:+91" + (sellerContact ? sellerContact.callNumber : "");

  return (
    <>
      {showLoginPrompt && (
        <div
          className="modal-overlay"
          onClick={() => setShowLoginPrompt(false)}
        >
          <div
            className="modal-glass"
            onClick={(e) => e.stopPropagation()}
            style={{ textAlign: "center", maxWidth: 360 }}
          >
            <div style={{ fontSize: "2.5rem", marginBottom: "var(--space-3)" }}>
              🔒
            </div>
            <h3 className="modal-title">Sign in to continue</h3>
            <p className="modal-message">
              You need a GLA University account to contact sellers or add items
              to your cart.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowLoginPrompt(false)}
              >
                Cancel
              </button>
              <Link to="/login" className="btn btn-primary">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      )}
      <div className="pd-page fade-in">
        {/* Report Modal */}
        {showReport && (
          <div className="modal-overlay" onClick={() => setShowReport(false)}>
            <div
              className="modal-glass report-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="report-modal-header">
                <h3 className="modal-title">Report Listing</h3>
                <button
                  className="btn btn-ghost btn-icon"
                  onClick={() => setShowReport(false)}
                >
                  <X size={18} />
                </button>
              </div>

              <p
                style={{
                  fontSize: "0.82rem",
                  color: "var(--text-muted)",
                  marginBottom: "var(--space-4)",
                }}
              >
                Report "{product.title}" — our admin team will review it within
                24 hours.
              </p>

              <form
                onSubmit={handleReport}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-4)",
                }}
              >
                <div className="form-group">
                  <label className="label">Reason</label>
                  <div className="report-reasons">
                    {REPORT_REASONS.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        className={
                          "report-reason-btn " +
                          (reportReason === r.value ? "active" : "")
                        }
                        onClick={() => setReportReason(r.value)}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="label">Additional details (optional)</label>
                  <textarea
                    className="textarea"
                    placeholder="Describe the issue in more detail..."
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
                    rows={3}
                    maxLength={500}
                  />
                </div>

                <div style={{ display: "flex", gap: "var(--space-3)" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                    onClick={() => setShowReport(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-danger"
                    style={{ flex: 1 }}
                    disabled={reporting || !reportReason}
                  >
                    {reporting ? (
                      <>
                        <div
                          className="spinner"
                          style={{ width: 14, height: 14 }}
                        />{" "}
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Flag size={14} /> Submit Report
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <button
          className="btn btn-ghost btn-icon pd-back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={20} />
        </button>

        <div className="pd-grid">
          {/* ── Left: image gallery ── */}
          <div className="pd-gallery">
            <div className="pd-main-img">
              <img src={product.images[activeImg]} alt={product.title} />
              {isRemoved && (
                <div className="pd-removed-overlay">
                  <span>This listing has been removed</span>
                </div>
              )}
            </div>

            {product.images.length > 1 && (
              <div className="pd-thumbs">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    className={
                      "pd-thumb" + (activeImg === idx ? " active" : "")
                    }
                    onClick={() => setActiveImg(idx)}
                  >
                    <img src={img} alt={"View " + (idx + 1)} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Right: details ── */}
          <div className="pd-details">
            {/* Badges + share row */}
            <div className="pd-top-row">
              <div className="pd-badges">
                <span className={"badge badge-" + product.category}>
                  {CATEGORY_LABELS[product.category] || product.category}
                </span>
                {isRemoved && (
                  <span className="badge badge-danger">Removed</span>
                )}
              </div>

              {/* Share + Copy + Report */}
              <div className="pd-action-icons">
                <button
                  className="pd-icon-btn"
                  onClick={handleShare}
                  title="Share on WhatsApp"
                >
                  <Share2 size={16} />
                  <span>Share</span>
                </button>
                <button
                  className="pd-icon-btn"
                  onClick={handleCopyLink}
                  title="Copy link"
                >
                  🔗
                  <span>Copy</span>
                </button>
                {!isOwn && !reported && user && (
                  <button
                    className="pd-icon-btn danger"
                    onClick={() => setShowReport(true)}
                    title="Report listing"
                  >
                    <Flag size={16} />
                    <span>Report</span>
                  </button>
                )}
                {reported && (
                  <span
                    style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}
                  >
                    ✓ Reported
                  </span>
                )}
              </div>
            </div>

            <h2 className="pd-title">{product.title}</h2>

            <div className="pd-price">₹{product.price.toLocaleString()}</div>

            {product.quantity > 1 && (
              <p className="text-muted" style={{ fontSize: "0.875rem" }}>
                <Package
                  size={14}
                  style={{ display: "inline", marginRight: 4 }}
                />
                {product.quantity} available
              </p>
            )}

            <div className="pd-desc-section">
              <h4>Description</h4>
              <p className="pd-desc">{product.description}</p>
            </div>

            {/* ── Actions ── */}
            {!isOwn && !isRemoved && (
              <div className="pd-actions">
                {product.chatOnly ? (
                  // ── Female seller: chat only, no phone reveal ─────────
                  <div className="pd-contact-revealed">
                    <div className="pd-contact-success">
                      <MessageCircle size={18} color="var(--accent)" />
                      <span>Contact via website chat only</span>
                    </div>
                    <button
                      className="btn btn-primary btn-full btn-lg"
                      onClick={handleChat}
                    >
                      <MessageCircle size={17} /> Chat with Seller
                    </button>
                  </div>
                ) : sellerContact ? (
                  // ── Contact already revealed ──────────────────────────
                  <div className="pd-contact-revealed">
                    <div className="pd-contact-success">
                      <CheckCircle size={18} color="var(--success)" />
                      <span>Seller contact revealed</span>
                    </div>
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-success btn-full"
                    >
                      <MessageCircle size={17} />
                      {"WhatsApp " + sellerContact.sellerName}
                    </a>
                    <a href={callLink} className="btn btn-secondary btn-full">
                      <Phone size={17} />
                      {"Call " + sellerContact.callNumber}
                    </a>
                    <button
                      className="btn btn-primary btn-full"
                      onClick={handleChat}
                    >
                      <MessageCircle size={17} />
                      {"Chat with " + sellerContact.sellerName}
                    </button>
                  </div>
                ) : (
                  // ── Default: reveal contact button ────────────────────
                  <button
                    className="btn btn-primary btn-full btn-lg"
                    onClick={handleContact}
                    disabled={contacting}
                  >
                    {contacting ? (
                      <span className="flex-center flex-gap-2">
                        <div className="spinner" /> Getting contact...
                      </span>
                    ) : (
                      <span className="flex-center flex-gap-2">
                        <Phone size={17} /> Contact Seller
                      </span>
                    )}
                  </button>
                )}

                <button
                  className={
                    "btn btn-full " + (inCart ? "btn-success" : "btn-secondary")
                  }
                  onClick={handleAddToCart}
                  disabled={addingCart || inCart}
                >
                  {addingCart ? (
                    <span className="flex-center flex-gap-2">
                      <div className="spinner" /> Adding...
                    </span>
                  ) : inCart ? (
                    <span className="flex-center flex-gap-2">
                      <CheckCircle size={16} /> Added to Cart
                    </span>
                  ) : (
                    <span className="flex-center flex-gap-2">
                      <ShoppingCart size={16} /> Add to Cart
                    </span>
                  )}
                </button>
              </div>
            )}

            {isOwn && (
              <div className="pd-own-notice">
                This is your listing.{" "}
                <Link
                  to={"/edit-product/" + product._id}
                  className="text-accent"
                >
                  Edit it →
                </Link>
              </div>
            )}

            {/* ── Seller card ── */}
            <div className="pd-seller card">
              <h4 style={{ marginBottom: "var(--space-4)" }}>Seller</h4>

              <div className="pd-seller-profile">
                {seller && seller.avatar ? (
                  <img
                    src={seller.avatar}
                    alt={seller.name}
                    className="avatar avatar-md"
                  />
                ) : (
                  <div className="avatar-placeholder avatar-md">
                    {seller && seller.name ? seller.name[0] : "?"}
                  </div>
                )}
                <div>
                  <div className="pd-seller-name">
                    {seller ? seller.name : "Unknown"}
                  </div>
                  <div className="pd-seller-roll text-muted">
                    {seller ? seller.rollNumber : ""}
                  </div>
                </div>
              </div>

              <div className="pd-seller-stats">
                <div className="pd-seller-stat">
                  <span className="pd-seller-stat-val">
                    {seller ? seller.sellCount || 0 : 0}
                  </span>
                  <span className="pd-seller-stat-lbl">Total Sells</span>
                </div>
                <div className="pd-seller-stat">
                  <span className="pd-seller-stat-val">
                    {seller ? seller.activeListings || 0 : 0}
                  </span>
                  <span className="pd-seller-stat-lbl">Active Listings</span>
                </div>
                <div className="pd-seller-stat">
                  <span className="pd-seller-stat-val">
                    {product.contactCount}
                  </span>
                  <span className="pd-seller-stat-lbl">Interests</span>
                </div>
              </div>

              {seller && seller.location && (
                <div className="pd-seller-location text-muted">
                  <MapPin size={13} />
                  {seller.location}
                </div>
              )}

              {seller && (
                <Link
                  to={"/app/user/" + seller._id}
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: "var(--space-3)" }}
                >
                  View Profile
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
